import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as apicall from "../Apicall";
import { useWebSocket } from "../../hooks/useWebSocket";
import useViewport from "../../hooks/useViewport";

// WhatsApp calling for the whole vendor portal. Lives in the app layout, not in
// the Channels page, so a customer's call rings (sound, popup, tab title,
// browser notification) on whatever screen the team is looking at. The Channels
// page uses it for the Call button via useCalls().

const avatarColors = ["#6c5ce7","#00b894","#0984e3","#e17055","#fd79a8","#fdcb6e","#55efc4"];
const getAvatarColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const NOOP_CALLS = {
  incomingCall: null,
  activeCall: null,
  startingCall: false,
  startOutboundCall: async () => ({ error: "Calling is not available for this account" }),
};
const CallContext = createContext(NOOP_CALLS);
export const useCalls = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
  const navigate = useNavigate();
  // userDetails can hold a non-JSON value (e.g. the text "undefined" from an empty profile
  // response); treat that as no details rather than crashing the whole layout.
  let vendorDetails = {};
  try {
    vendorDetails = JSON.parse(localStorage.getItem("userDetails") || "{}") || {};
  } catch {
    vendorDetails = {};
  }
  const currentUserId = localStorage.getItem("userId") || String(vendorDetails?.id || "");
  const isEmployee = Number(apicall.getRole() || 0) === 6;
  const { isPhone: isMobile } = useViewport();

  const [incomingCall, setIncomingCall] = useState(null);   // { call_id, customer_id, customer_name, customer_phone, sdp, ... }
  const [activeCall, setActiveCall] = useState(null);       // { call_id, customer_id, name, outbound?, status? }
  const [acceptingCall, setAcceptingCall] = useState(false);
  const [callMuted, setCallMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [soundReady, setSoundReady] = useState(false);
  const [startingCall, setStartingCall] = useState(false);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const ringTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const titleTimerRef = useRef(null);
  const notifRef = useRef(null);

  // ── WhatsApp calls (customer rings the vendor's number) ─────────────────────
  // The backend pushes "incoming_call" with Meta's connection offer. Accepting
  // builds the audio connection in this browser (WebRTC), sends our answer to
  // the backend (which relays it to Meta), and the customer's voice plays
  // through the hidden <audio>. The first team member to accept wins.
  // Browsers keep audio switched off until the user has clicked / pressed a key
  // on the page, and a call alert arrives on its own — so the sound is unlocked
  // on the first interaction (see the effect below) and a hint is shown until then.
  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };
  const unlockSound = () => {
    try {
      const ctx = getAudioCtx();
      if (!ctx) return;
      const done = () => setSoundReady(ctx.state === "running");
      if (ctx.state === "suspended") ctx.resume().then(done).catch(() => {});
      else done();
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) { /* ignore */ }
  };

  // Classic phone ring: two short double-tones, then a pause, repeated.
  const playRingBurst = () => {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state !== "running") return;
    const now = ctx.currentTime;
    [0, 0.5].forEach((offset) => {
      [440, 480].forEach((freq) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, now + offset);
        g.gain.exponentialRampToValueAtTime(0.25, now + offset + 0.03);
        g.gain.setValueAtTime(0.25, now + offset + 0.38);
        g.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.42);
        o.connect(g); g.connect(ctx.destination);
        o.start(now + offset);
        o.stop(now + offset + 0.45);
      });
    });
  };

  const startRing = (callerLabel) => {
    if (ringTimerRef.current) return;
    try {
      const ctx = getAudioCtx();
      const kick = () => { playRingBurst(); };
      if (ctx && ctx.state === "suspended") {
        ctx.resume().then(() => { setSoundReady(ctx.state === "running"); kick(); }).catch(() => {});
      } else {
        kick();
      }
      ringTimerRef.current = setInterval(kick, 2600);
    } catch (e) { /* the popup still shows */ }

    // Also make the call hard to miss from another tab / window.
    const originalTitle = document.title;
    let flip = false;
    titleTimerRef.current = setInterval(() => {
      flip = !flip;
      document.title = flip ? "📞 Incoming call…" : originalTitle;
    }, 900);
    titleTimerRef.current.originalTitle = originalTitle;
    try {
      if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
        const n = new Notification("📞 Incoming WhatsApp call", {
          body: callerLabel || "A customer is calling",
          requireInteraction: true,
          tag: "wa-call",
        });
        n.onclick = () => { window.focus(); n.close(); };
        notifRef.current = n;
      }
    } catch (e) { /* notifications are optional */ }
  };
  const stopRing = () => {
    if (ringTimerRef.current) clearInterval(ringTimerRef.current);
    ringTimerRef.current = null;
    if (titleTimerRef.current) {
      clearInterval(titleTimerRef.current);
      document.title = titleTimerRef.current.originalTitle || document.title;
      titleTimerRef.current = null;
    }
    try { notifRef.current?.close(); } catch (e) {}
    notifRef.current = null;
  };
  const dismissIncomingCall = () => { stopRing(); setIncomingCall(null); };

  const cleanupCall = () => {
    try { pcRef.current?.close(); } catch (e) {}
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    setActiveCall(null);
    setCallMuted(false);
    setCallSeconds(0);
  };

  const waitForIce = (pc) => new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    const done = () => { pc.removeEventListener("icegatheringstatechange", onChange); resolve(); };
    const onChange = () => { if (pc.iceGatheringState === "complete") done(); };
    pc.addEventListener("icegatheringstatechange", onChange);
    setTimeout(done, 3000); // don't hold the ringing call for slow candidates
  });

  const acceptCall = async () => {
    const call = incomingCall;
    if (!call || acceptingCall) return;
    if (!call.sdp) { toast.error("Call details are missing — can't answer this call"); dismissIncomingCall(); return; }
    setAcceptingCall(true);
    stopRing();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = e.streams[0];
          remoteAudioRef.current.play().catch(() => {});
        }
      };
      await pc.setRemoteDescription({ type: "offer", sdp: call.sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForIce(pc);

      const res = await apicall.post({
        call_id: call.call_id,
        action: "accept",
        sdp: pc.localDescription.sdp,
      }, "callAction");
      if (!res?.success) throw new Error(res?.message || "Could not answer the call");

      setActiveCall({
        call_id: call.call_id,
        customer_id: call.customer_id,
        name: call.customer_name || call.customer_phone,
      });
      setIncomingCall(null);
      // Open this customer's chat on the Channels screen (from wherever we are).
      navigate("/channel/dashboard", { state: { platform: "whatsapp", openCustomerId: call.customer_id } });
    } catch (e) {
      console.log(e);
      const denied = e?.name === "NotAllowedError";
      toast.error(denied ? "Microphone access is blocked — allow it in the browser to take calls"
                         : (e?.message || "Could not answer the call"));
      cleanupCall();
      setIncomingCall(null);
    } finally {
      setAcceptingCall(false);
    }
  };

  const declineCall = async () => {
    const call = incomingCall;
    dismissIncomingCall();
    if (!call) return;
    try { await apicall.post({ call_id: call.call_id, action: "reject" }, "callAction"); }
    catch (e) { console.log(e); }
  };

  const hangUpCall = async () => {
    const call = activeCall;
    cleanupCall();
    if (!call) return;
    try { await apicall.post({ call_id: call.call_id, action: "terminate" }, "callAction"); }
    catch (e) { console.log(e); }
  };

  const toggleCallMute = () => {
    const next = !callMuted;
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setCallMuted(next);
  };


  // ── Calling the customer ───────────────────────────────────────────────────
  // WhatsApp only lets a business ring a customer who has allowed it. Returns
  // { ok: true } | { needPermission: true, denied? } | { error } so the caller
  // (the Channels page) can offer to send the permission request.
  const startOutboundCall = async (customer) => {
    if (!customer?.customer_id || startingCall || activeCall || incomingCall) return { error: "busy" };
    setStartingCall(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = e.streams[0];
          remoteAudioRef.current.play().catch(() => {});
        }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIce(pc);

      const res = await apicall.post({
        customer_id: customer.customer_id,
        sdp: pc.localDescription.sdp,
      }, "startCall");

      if (res?.need_permission) {
        cleanupCall();
        return { needPermission: true, denied: !!res.denied };
      }
      if (!res?.success) throw new Error(res?.message || "Could not place the call");

      setActiveCall({
        call_id: res.call_id,
        customer_id: customer.customer_id,
        name: customer.name,
        outbound: true,
        status: "ringing",
      });
      setCallSeconds(0);
      return { ok: true };
    } catch (e) {
      console.log(e);
      cleanupCall();
      const msg = e?.name === "NotAllowedError"
        ? "Microphone access is blocked — allow it in the browser to place calls"
        : (e?.message || "Could not place the call");
      toast.error(msg);
      return { error: msg };
    } finally {
      setStartingCall(false);
    }
  };

  // Call timer while a call is live; ringing popup gives up after a minute
  // (Meta ends an unanswered call about then anyway).
  useEffect(() => {
    if (!activeCall || activeCall.status === "ringing") return undefined;
    const t = setInterval(() => setCallSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [activeCall]);
  useEffect(() => {
    if (!incomingCall) return undefined;
    const t = setTimeout(() => dismissIncomingCall(), 60000);
    return () => clearTimeout(t);
  }, [incomingCall]);
  useEffect(() => () => { stopRing(); cleanupCall(); }, []);
  // Unlock call sounds on the first click / key press / tap anywhere on the page.
  useEffect(() => {
    const events = ["pointerdown", "keydown", "touchstart"];
    const onFirst = () => {
      unlockSound();
      events.forEach((e) => window.removeEventListener(e, onFirst));
    };
    events.forEach((e) => window.addEventListener(e, onFirst));
    return () => events.forEach((e) => window.removeEventListener(e, onFirst));
  }, []);


  // ── Live call events from the server ───────────────────────────────────────
  useWebSocket({
    onEvent: (data) => {
      if (data.type === "incoming_call") {
        // The vendor sees every call; a team member only calls on unassigned
        // chats or chats assigned to them (same rule as replying).
        const mine = !isEmployee || !data.assigned_user_id
          || String(data.assigned_user_id) === String(currentUserId);
        if (mine && !activeCall) {
          setIncomingCall(data);
          startRing(data.customer_name || data.customer_phone);
        }
      } else if (data.type === "call_taken") {
        // Someone else answered/declined — drop our ringing popup.
        if (incomingCall?.call_id === data.call_id && String(data.answered_by) !== String(currentUserId)) {
          dismissIncomingCall();
        }
      } else if (data.type === "outgoing_call_answer") {
        // The customer picked up: finish the audio link with Meta's answer.
        const pc = pcRef.current;
        if (activeCall?.outbound && String(activeCall.customer_id) === String(data.customer_id)
            && pc && pc.signalingState === "have-local-offer" && data.sdp) {
          pc.setRemoteDescription({ type: "answer", sdp: data.sdp })
            .then(() => { setCallSeconds(0); setActiveCall((c) => (c ? { ...c, status: "live" } : c)); })
            .catch((e) => console.log("[CALL] answer failed", e));
        }
      } else if (data.type === "call_ended") {
        if (incomingCall?.call_id === data.call_id) dismissIncomingCall();
        if (activeCall?.call_id === data.call_id) {
          const unanswered = activeCall.outbound && activeCall.status === "ringing";
          cleanupCall();
          toast.info(unanswered ? "Call not answered" : "Call ended");
        }
      }
    },
  });

  const value = { incomingCall, activeCall, startingCall, startOutboundCall };

  return (
    <CallContext.Provider value={value}>
      {children}
      {/* Browsers block sound until the page has been clicked once */}
      {/* {!soundReady && (
        <div
          onClick={unlockSound}
          style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: isMobile ? "calc(78px + env(safe-area-inset-bottom, 0px))" : 14, zIndex: 9000, background: "#1a3a5f", color: "#fff", borderRadius: 20, padding: "7px 16px", fontSize: 12, cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,0.25)" }}
        >
          🔔 Click anywhere on the page once to turn on the call ring sound
        </div>
      )} */}

      {/* Customer's voice during a WhatsApp call */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: "none" }} />

      {/* Ringing: customer is calling the vendor's WhatsApp number */}
      {incomingCall && (
        <div style={{
          position: "fixed", right: 20, bottom: isMobile ? "calc(80px + env(safe-area-inset-bottom, 0px))" : 20, zIndex: 9500,
          width: isMobile ? "calc(100vw - 32px)" : 320,
          background: "#fff", borderRadius: 16, padding: 18,
          boxShadow: "0 12px 40px rgba(0,0,0,0.28)", border: "2px solid #25d366",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#25d366", letterSpacing: 0.6, marginBottom: 10 }}>
            📞 INCOMING WHATSAPP CALL
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{
              width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
              background: getAvatarColor(incomingCall.customer_name || incomingCall.customer_phone),
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: 18,
            }}>
              {(incomingCall.customer_name || incomingCall.customer_phone || "?")[0]?.toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {incomingCall.customer_name || incomingCall.customer_phone}
              </div>
              {incomingCall.customer_name && (
                <div style={{ fontSize: 12, color: "#888" }}>{incomingCall.customer_phone}</div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={declineCall}
              disabled={acceptingCall}
              style={{ flex: 1, border: "none", borderRadius: 10, padding: "10px 0", background: "#e53935", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
            >
              Decline
            </button>
            <button
              onClick={acceptCall}
              disabled={acceptingCall}
              style={{ flex: 1, border: "none", borderRadius: 10, padding: "10px 0", background: "#25d366", color: "#fff", fontWeight: 600, fontSize: 14, cursor: acceptingCall ? "default" : "pointer", opacity: acceptingCall ? 0.7 : 1 }}
            >
              {acceptingCall ? "Connecting…" : "Accept"}
            </button>
          </div>
        </div>
      )}

      {/* On a call */}
      {activeCall && (
        <div style={{
          position: "fixed", right: 20, bottom: isMobile ? "calc(80px + env(safe-area-inset-bottom, 0px))" : 20, zIndex: 9500,
          width: isMobile ? "calc(100vw - 32px)" : 320,
          background: "#1a3a5f", color: "#fff", borderRadius: 16, padding: "14px 16px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              📞 {activeCall.name}
            </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              {activeCall.status === "ringing"
                ? "Ringing…"
                : `${String(Math.floor(callSeconds / 60)).padStart(2, "0")}:${String(callSeconds % 60).padStart(2, "0")}`}
            </div>
          </div>
          {activeCall.status !== "ringing" && (
          <button
            onClick={toggleCallMute}
            style={{ border: "none", borderRadius: 8, padding: "8px 12px", background: callMuted ? "#ffc107" : "rgba(255,255,255,0.18)", color: callMuted ? "#1a1a1a" : "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
          >
            {callMuted ? "Unmute" : "Mute"}
          </button>
          )}
          <button
            onClick={hangUpCall}
            style={{ border: "none", borderRadius: 8, padding: "8px 14px", background: "#e53935", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
          >
            {activeCall.status === "ringing" ? "Cancel" : "End"}
          </button>
        </div>
      )}

    </CallContext.Provider>
  );
};

export default CallProvider;
